import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";
import { marked } from "marked";
import { ChatRuntimeProvider } from "@src/features/assistantChat/ChatRuntimeProvider";
import NewCourseDialog from "@src/features/contentLibrary/NewCourseDialog";
import CourseOutlineCanvasDialog from "@src/features/contentLibrary/CourseOutlineCanvasDialog";
import { AiChatType } from "@src/types.generated";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { transformToSelectedSession } from "@src/features/aiAssist/utils/sessionTransform";
import type { AiChatFragment } from "@src/features/assistantChat/aiChat.generated";

type BaseCourseShape = { id: number; libraryItem: { id: string } };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Optional override for completion handling; if omitted, default routing is applied
  onCourseCreated?: (course: BaseCourseShape) => void;
  initialInput?: string;
  /**
   * A ready-made outline (markdown). When provided, skip the prompt step and
   * open straight to the outline canvas seeded with this text, so the user can
   * review/edit before handing off to the product container agent.
   */
  initialOutline?: string;
};

const CourseCreationFlowDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  onCourseCreated,
  initialInput,
  initialOutline,
}) => {
  const router = useRouter();
  const [showCanvas, setShowCanvas] = useState(false);
  const { selectSession } = useChatSidebar(AiChatType.CourseOutline);
  const seededOutlineRef = useRef(false);

  useEffect(() => {
    if (!open) setShowCanvas(false);
  }, [open]);

  // Seed the canvas with the provided outline once per open, then jump to it.
  useEffect(() => {
    if (!open) {
      seededOutlineRef.current = false;
      return;
    }
    if (!initialOutline || seededOutlineRef.current) return;
    seededOutlineRef.current = true;
    const html = marked.parse(initialOutline);
    selectSession({
      id: uuidv4(),
      title: "Course outline",
      createdAt: new Date().toISOString(),
      messages: [],
      artifact: typeof html === "string" ? html : "",
      artifactLoading: false,
      artifactMessageId: null,
    });
    setShowCanvas(true);
  }, [open, initialOutline, selectSession]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setShowCanvas(false);
    onOpenChange(nextOpen);
  };

  const handleSelectHistoricalChat = (chat: AiChatFragment) => {
    // Transform and load the historical chat
    const selectedSession = transformToSelectedSession(chat);
    selectSession(selectedSession);

    // Skip to canvas since we have an existing conversation
    setShowCanvas(true);
  };

  return (
    <ChatRuntimeProvider chatType={AiChatType.CourseOutline}>
      {/* Step 1: Prompt/options dialog */}
      <NewCourseDialog
        open={open && !showCanvas}
        onOpenChange={handleOpenChange}
        onStartNewChat={() => setShowCanvas(true)}
        onCourseCreated={onCourseCreated}
        initialInput={initialInput}
        enableChatHistory={true}
        onSelectHistoricalChat={handleSelectHistoricalChat}
      />

      <CourseOutlineCanvasDialog
        dialogIsOpen={open && showCanvas}
        setDialogIsOpen={(isOpen) => {
          if (!isOpen) handleOpenChange(false);
        }}
        onCourseCreated={(course: BaseCourseShape) => {
          if (onCourseCreated) {
            onCourseCreated(course);
            return;
          }
          const libraryItemId = course?.libraryItem?.id;
          if (libraryItemId) {
            router.push({
              pathname: "/library/library-item/[id]",
              query: { id: libraryItemId },
            });
          }
        }}
      />
    </ChatRuntimeProvider>
  );
};

export default CourseCreationFlowDialog;
